import { Inject, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { CurrencyPipe, PercentPipe }                  from '@angular/common';

@Directive({
  selector: '[format]',
  providers: [
    CurrencyPipe,
    PercentPipe
  ]
})

export class FormatterDirective {
  @Input('format') formatType: string;

  private el: HTMLInputElement;

  constructor(
    @Inject(ElementRef) private element: ElementRef,
    private currencyPipe: CurrencyPipe,
    private percentPipe: PercentPipe,
  ) {
    this.el = this.element.nativeElement;
  }

  @HostListener('keypress', ['$event']) onKeyDown(event: any) {
    switch (this.formatType) {
      case 'phone':
        if (this.shouldAllowInput(event, 9, false))
          this.formatPhone(this.el.value);
        else
          return false;
        break;
      case 'ssn':
        if (this.shouldAllowInput(event, 8, false))
          this.formatSSN(this.el.value);
        else
          return false;
        break;
      case 'ein':
        if (this.shouldAllowInput(event, 8, false))
          this.formatEIN(this.el.value);
        else
          return false;
        break;
      case 'currency':
        return this.shouldAllowInput(event, 8, true);
      case 'percentage':
        return this.shouldAllowInput(event, 3, true);
      case 'zipCode':
        return this.shouldAllowInput(event, 4, false);
      default:
        break;
    }
  }

  @HostListener('keyup', ['$event']) onKeyUp(event: any) {
    let key = event.keyCode;
    switch (this.formatType) {
      case 'currency':
        if (key >= 37 && key <= 40) break;
        this.formatCurrency(this.el.value, event);
        break;
      case 'percentage':
        // if (key >= 37 && key <= 40) break;
        // this.el.value = this.formatPercentage(this.el.value, event);
        break;
      case 'ssn':
        this.deleteNpi(event, 8);
        break;
      case 'ein':
        this.deleteNpi(event, 8);
        break;
      default:
        break;
    }
  }

  @HostListener('focus') onFocus(event: any) {
    switch (this.formatType) {
      default:
        break;
    }
  }

  @HostListener('blur') onBlur(event: any) {
    switch (this.formatType) {
      case 'phone':
        this.formatPhoneOnBlur(this.el.value);
        break;
      case 'ssn':
        this.formatSSNOnBlur(this.el.value);
        break;
      case 'ein':
        this.formatEINOnBlur(this.el.value);
        break;
      default:
        break;
    }
  }

  private shouldAllowInput(event: any, maxLength: number, allowDecimal: boolean): boolean {
    let key = event.keyCode;
    let regex = allowDecimal ? new RegExp('^[0-9.]+$') : new RegExp('^[0-9]+$');
    let sanitizedNumber = this.el.value.replace(/[^0-9X]+/g, '');

    if (key !== 0 && key !== 8 && key !== 13 && key !== 9) { // ignore backspace
      if (sanitizedNumber.length > maxLength) {
        event.preventDefault();
        return false;
      }
      let keyCode = String.fromCharCode(key);
      if (!regex.test(keyCode)) {
        event.preventDefault();
        return false;
      }
    }

    return true;
  }

  private deleteNpi(event: any, len: number) {
    let key = event.keyCode;
    if (key !== 0 && key !== 8 && key !== 13 && key !== 9) { // check for backspace
      return;
    }
    let sanitizedNumber = this.el.value.split('-').join('');
    if (sanitizedNumber.length === len) {
      this.el.value = '';
    }
  }

  private formatPhone(value: string) {
    let sanitizedValue = this.sanitizeNumber(value);
    if (sanitizedValue.length === 3)
      this.el.value = '(' + sanitizedValue + ') ';
    else if (sanitizedValue.length === 6 && value.slice(4, 6) === ') '
      && value.substr(value.length - 1) !== '-')
      this.el.value = value + '-';
    else if (sanitizedValue.length === 10)
      this.el.value = sanitizedValue.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }

  private formatPhoneOnBlur(value: string) {
    let sanitizedPhoneNumber = this.sanitizeNumber(value);
    if (sanitizedPhoneNumber.length === 10) {
      this.el.value = sanitizedPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
  }

  private formatSSN(value: string) {
    let sanitizedValue = this.sanitizeNumber(value);
    if (sanitizedValue.length === 3 && value.substr(value.length - 1) !== '-')
      this.el.value = sanitizedValue + '-';
    else if (sanitizedValue.length === 5 && value.substr(value.length - 1) !== '-')
      this.el.value = value + '-';
  }

  private formatSSNOnBlur(value: string) {
    let sanitizedSSN = this.sanitizeNumber(value);
    // this.appState.setHash(this.el.id, sanitizedSSN);
    if (sanitizedSSN.length === 9) {
      // this.appState.setHash(this.el.id,
      //   sanitizedSSN.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3'));
      let mask = 'XXX-XX-' + sanitizedSSN.substr(sanitizedSSN.length - 4);
      this.el.value = mask;
    }
  }

  private formatEIN(value: string) {
    let sanitizedValue = this.sanitizeNumber(value);
    if (sanitizedValue.length === 2 && value.substr(value.length - 1) !== '-')
      this.el.value = sanitizedValue + '-';
  }

  private formatEINOnBlur(value: string) {
    let sanitizedEIN = this.sanitizeNumber(value);
    // this.appState.setHash(this.el.id, sanitizedEIN);
    if (sanitizedEIN.length === 9) {
      // this.appState.setHash(this.el.id,
      //   sanitizedEIN.replace(/(\d{2})(\d{7})/, '$1-$2'));
      let mask = 'XX-XXX' + sanitizedEIN.substr(sanitizedEIN.length - 4);
      this.el.value = mask;
    }
  }

  private formatCurrency(value: string, event: any) {
    let sanitizedValue = this.sanitizeNumber(value);
    if (sanitizedValue.length > 0)
      this.el.value = this.currencyPipe.transform(sanitizedValue, 'USD', true, '1.0');
  }

  private sanitizeNumber(value) {
    return value.replace(/[^\d]+/g, '');
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
